import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { money } from "../../lib/shop";
import { useSite } from "../../context/SiteContext";

const field = "w-full rounded-xl border border-[#EBD08A] px-3 py-2.5 outline-none focus:border-[#FF1F8E]";

export const ShopDetails = ({ order }) => order.kind !== "shop" ? null : (
  <div className="mt-6 rounded-2xl bg-[#FFF4F8] p-4 text-sm" data-testid="admin-shop-details">
    <p className="font-semibold">Shop order · {order.fulfillment === "ship" ? "Ship" : "Pickup"}</p>
    <ul className="mt-2 space-y-1">{order.pieces.map((p, i) => <li key={i}>{p.label}: <b>{p.color}</b>, size <b>{p.size}</b>{p.personalization && ` · personalization: "${p.personalization}"`}</li>)}</ul>
    {order.shipping_address && <p className="mt-2">Ship to: {order.shipping_address}</p>}
    <p className="mt-2">Subtotal {money(order.subtotal)} · Shipping {money(order.shipping)} · Tax {money(order.tax)} · <b>Paid {money(order.amount_paid)}</b></p>
  </div>
);

export const PaymentTools = ({ order, onSaved }) => {
  const { site } = useSite();
  const [price, setPrice] = useState(order.final_price || "");
  const [via, setVia] = useState(order.paid_via || "");
  const [amount, setAmount] = useState(order.amount_paid || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPrice(order.final_price || ""); setVia(order.paid_via || ""); setAmount(order.amount_paid || ""); }, [order]);
  if (order.kind === "shop") return null;
  const isPaid = ["Paid", "In Production", "Completed"].includes(order.status);

  const savePrice = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, { final_price: Number(price) });
      const { data } = await api.post(`/admin/orders/${order.id}/pay-link`, { origin_url: window.location.origin });
      onSaved(data);
      toast.success("Pay link emailed to the customer");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const record = async () => {
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, { paid_via: via, amount_paid: Number(amount), status: "Paid" });
      onSaved(data);
      toast.success("Payment recorded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-6 grid gap-4 rounded-2xl bg-[#FFF4F8] p-4 sm:grid-cols-2" data-testid="admin-payment-tools">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A537]">Quote &amp; online payment</p>
        <div className="mt-2 flex gap-2">
          <input type="number" min="1" step="0.01" placeholder="Final price $" value={price} disabled={isPaid} onChange={(e) => setPrice(e.target.value)} className={field} data-testid="admin-final-price" />
          <button onClick={savePrice} disabled={busy || isPaid || !Number(price)} className="btn-pink shrink-0 !px-4 !py-2 text-sm disabled:opacity-50" data-testid="admin-send-pay-link"><Send className="h-4 w-4" />Send pay link</button>
        </div>
        <p className="mt-1 text-xs text-black/50">{isPaid ? "This order is already paid." : "Emails the customer a Pay Now button (card / Apple Pay). Marked Paid automatically."}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#D4A537]">Record a payment received</p>
        <div className="mt-2 flex gap-2">
          <select value={via} onChange={(e) => setVia(e.target.value)} className={field} data-testid="admin-paid-via"><option value="">Paid with…</option>{site.payments.map((p) => <option key={p}>{p}</option>)}</select>
          <input type="number" min="0" step="0.01" placeholder="$" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${field} !w-24`} data-testid="admin-amount-paid" />
          <button onClick={record} disabled={!via || !Number(amount)} className="btn-outline-gold shrink-0 !px-4 !py-2 text-sm disabled:opacity-50" data-testid="admin-record-payment">Save</button>
        </div>
        {order.paid_via && <p className="mt-1 text-xs font-semibold" data-testid="admin-paid-summary">Paid {money(order.amount_paid)} via {order.paid_via}</p>}
      </div>
    </div>
  );
};
